import { IkeToken, type LineSymbol, resolveLineSymbol } from '@ikegps/core-api';
import { MIDSPAN_SALMON } from '../../components/theme.palette';
import { T, Translate } from '../../i18n';
import { Services } from '../../services';
import { getSpanLineColor, type MaybeSpanType } from './map.line/map.line';
import type { PoleAccuracy } from './map.point/map.point';

/**
 * A legend swatch, described independently of how it is rendered. The on-screen `MapModeLegend`
 * (Mithril/CSS) and the print legend (pdfMake) both consume the same descriptor list so the two
 * can never drift in content, color, or order.
 */
export type LegendSwatch =
	| { kind: 'line'; color: string }
	| { kind: 'point'; accuracy: PoleAccuracy; selected?: boolean }
	| { kind: 'dot'; color: string }
	| { kind: 'collectionLabel' }
	| { kind: 'device' }
	| { kind: 'lineStyle'; symbol: LineSymbol }
	| {
			kind: 'assetType';
			icon: string;
			color: string;
			title: string;
			/** Self-contained `<svg>` markup for the recolored icon glyph, fetched server-side (print-only —
			 *  the on-screen legend renders the Material Icon font directly and never sets this). */
			svg?: string;
	  };

export type LegendItemId =
	| 'dropspan'
	| 'anchor'
	| 'span'
	| 'unknown'
	| 'collectionLabel'
	| 'photoLocation'
	| 'midspanPhoto'
	| 'selectedCollection'
	| 'rtk_fixed'
	| 'edited'
	| 'rtk_float'
	| 'low';

export interface LegendItem {
	id?: LegendItemId;
	swatch: LegendSwatch;
	/** i18n key for a fixed label; `label` takes precedence when both are set. */
	text?: T;
	/** Pre-resolved free-text label (used for per-form line-type names). */
	label?: string;
	/** Layer rows get a visibility eye in the on-screen legend; state rows (selected collection) do not. */
	toggleable?: boolean;
	/** Present on line-type rows; keys the per-user visibility toggle for that line type. */
	lineTypeKey?: string;
	/** Present on asset-type rows; keys the per-user visibility toggle for that asset type. */
	assetTypeKey?: string;
	/** Entitlement for the layer: ALL listed tokens are required. When the department lacks any of them,
	 *  neither the row nor its map layer renders. Absent = always granted. */
	tokens?: readonly IkeToken[];
}

/** Whether the department is entitled to an item: ALL listed tokens required, none listed = always granted. */
function isGranted(item: LegendItem): boolean {
	return item.tokens == null || item.tokens.every((token) => Services.Token.has(token));
}

/** Whether the department is entitled to a layer. Unknown ids are granted. */
export function isLegendItemTokenGranted(id: LegendItemId): boolean {
	const item = LEGEND_ITEMS.find((item) => item.id === id);
	return item == null || isGranted(item);
}

/**
 * The legend rows this department may see. Callers pass the per-form line-type rows, which carry no
 * entitlement of their own. Note `readDisplaySettings` deliberately validates persisted ids against the
 * unfiltered `LEGEND_ITEMS` — a user who loses a token keeps their hide/show choice for when it returns.
 */
export function grantedLegendItems(extraItems: LegendItem[] = []): LegendItem[] {
	return [...LEGEND_ITEMS, ...extraItems].filter(isGranted);
}

export function legendItemLabel(item: LegendItem): string {
	return item.label ?? (item.text != null ? Translate(item.text) : '');
}

/** Stable per-user visibility key for a line type — the same composite `lineStyleLegendItems` dedups on. */
export function lineTypeVisibilityKey(option: { symbolId: string; symbolColor: string; title: string }): string {
	return `${option.symbolId}|${option.symbolColor}|${option.title}`;
}

/** Dedupe on shape + color + title together, so two same-title forms with a different shape/color both get a row. */
export function lineStyleLegendItems(
	options: ReadonlyArray<{ symbolId: string; title: string; symbolColor: string }>
): LegendItem[] {
	const seen = new Set<string>();
	const items: LegendItem[] = [];
	for (const option of options) {
		const symbol = resolveLineSymbol(option);
		const key = lineTypeVisibilityKey(option);
		if (!symbol || seen.has(key)) {
			continue;
		}
		seen.add(key);
		items.push({ swatch: { kind: 'lineStyle', symbol }, label: option.title, lineTypeKey: key });
	}
	return items;
}

/** Stable per-user visibility key for an asset type — the same composite `assetTypeLegendItems` dedups on. */
export function assetTypeVisibilityKey(option: { icon: string; color: string; title: string }): string {
	return `${option.icon}|${option.color}|${option.title}`;
}

/** Dedupe on icon + color + title together, so two same-title forms with a different icon/color both get a row. */
export function assetTypeLegendItems(
	options: ReadonlyArray<{ icon: string; color: string; title: string }>
): LegendItem[] {
	const seen = new Set<string>();
	const items: LegendItem[] = [];
	for (const option of options) {
		const key = assetTypeVisibilityKey(option);
		if (seen.has(key)) {
			continue;
		}
		seen.add(key);
		items.push({
			swatch: { kind: 'assetType', icon: option.icon, color: option.color, title: option.title },
			label: option.title,
			assetTypeKey: key,
		});
	}
	return items;
}

export const LEGEND_ITEMS: LegendItem[] = [
	{
		id: 'dropspan',
		toggleable: true,
		swatch: { kind: 'line', color: getSpanLineColor('dropspan') },
		text: T.MAPMODE_LEGEND_DROPSPAN,
	},
	{
		id: 'anchor',
		toggleable: true,
		swatch: { kind: 'line', color: getSpanLineColor('anchor') },
		text: T.MAPMODE_LEGEND_ANCHOR,
	},
	{
		id: 'span',
		toggleable: true,
		swatch: { kind: 'line', color: getSpanLineColor('span') },
		text: T.MAPMODE_LEGEND_SPAN,
	},
	{
		id: 'unknown',
		toggleable: true,
		swatch: { kind: 'line', color: getSpanLineColor('unknown') },
		text: T.MAPMODE_LEGEND_UNKNOWN,
	},
	{
		id: 'collectionLabel',
		toggleable: true,
		swatch: { kind: 'collectionLabel' },
		text: T.MAPMODE_LEGEND_COLLECTION_LABEL,
	},
	{
		id: 'photoLocation',
		toggleable: true,
		swatch: { kind: 'device' },
		text: T.MAPMODE_LEGEND_PHOTO_LOCATION,
	},
	{
		id: 'midspanPhoto',
		toggleable: true,
		swatch: { kind: 'dot', color: MIDSPAN_SALMON },
		text: T.MAPMODE_LEGEND_MIDSPAN_PHOTO,
		// MidSpan map markers ship with the Verify integration, not with plain IKE Office Pro: the tool
		// token alone (which the IKE Analyze group grants) must not light this layer up (UFO-1082, UFO-1506).
		tokens: [IkeToken.FEATURE_MIDSPAN_PHOTO_TOOL, IkeToken.FEATURE_POLEOS_VERIFY_INTEGRATION],
	},
	{
		id: 'selectedCollection',
		toggleable: false,
		swatch: { kind: 'point', accuracy: 'rtk_fixed', selected: true },
		text: T.MAPMODE_LEGEND_SELECTED_COLLECTION,
	},
	{
		id: 'rtk_fixed',
		toggleable: true,
		swatch: { kind: 'point', accuracy: 'rtk_fixed' },
		text: T.MAPMODE_LEGEND_LOCATION_RTK_FIXED,
	},
	{
		id: 'edited',
		toggleable: true,
		swatch: { kind: 'point', accuracy: 'edited' },
		text: T.MAPMODE_LEGEND_LOCATION_REVIEWED,
	},
	{
		id: 'rtk_float',
		toggleable: true,
		swatch: { kind: 'point', accuracy: 'rtk_float' },
		text: T.MAPMODE_LEGEND_LOCATION_RTK_FLOAT,
	},
	{
		id: 'low',
		toggleable: true,
		swatch: { kind: 'point', accuracy: 'low' },
		text: T.MAPMODE_LEGEND_LOCATION_LOW_ACCURACY,
	},
];

/** The legend row governing a span/vector line kind ('vector-anchor' shares the anchor row). */
export function legendItemIdForSpanKind(kind: MaybeSpanType): LegendItemId {
	return kind === 'vector-anchor' ? 'anchor' : kind;
}

/** The legend row governing a pole marker accuracy, or null when no row exists (never hidden). */
export function legendItemIdForPoleAccuracy(accuracy: PoleAccuracy): LegendItemId | null {
	switch (accuracy) {
		case 'rtk_fixed':
			return 'rtk_fixed';
		case 'edited':
			return 'edited';
		case 'rtk_float':
		case 'manual':
			return 'rtk_float';
		case 'low':
			return 'low';
		default:
			return null;
	}
}
